/**
 * Auth Session Service
 * Single source of truth for how this client authenticates.
 *
 * Shared by:
 *  - the intercepted `api` client (online requests),
 *  - the offline sync-queue replay (`syncQueue.ts`),
 *  - the Service Worker (via the IndexedDB `config` write-through below).
 *
 * Two modes, decided by the server
 * -------------------------------
 * COOKIE mode — the server issued HttpOnly session cookies. The browser attaches
 * them on its own, so nothing credential-like is stored here: we keep only the
 * CSRF token, which is meant to be readable and grants no access by itself.
 * This is the target state, and it is what closes the XSS hole: a JWT that is
 * never written to localStorage cannot be read out of it.
 *
 * BEARER mode — the legacy scheme. JWTs live in localStorage and travel in the
 * Authorization header.
 *
 * The mode is not configured client-side; it is DETECTED. `/auth/login` and
 * `/auth/refresh` return a `csrf_token` only when the server actually issued
 * cookies. That single signal is unambiguous, works identically in the Service
 * Worker (which can read neither document.cookie nor localStorage), and lets a
 * server rollback of AUTH_COOKIE_ENABLED bring clients back to Bearer on their
 * next login without any coordination.
 *
 * Offline note
 * ------------
 * Queued write operations are replayed long after they were recorded — often
 * well past the 30-minute access-token lifetime. Nothing credential-like is
 * ever frozen into a queued item: in cookie mode the browser attaches fresh
 * cookies at replay time, and in bearer mode `getValidAccessToken()` renews the
 * token from the long-lived refresh token.
 */

import axios from 'axios';

import { deleteConfig, putConfig } from './offlineDb';
import { getApiBaseUrl } from '../config/env';

// ─── Storage keys ──────────────────────────────────────────────────────────────

/** Legacy JWT keys. Kept only so bearer mode and the purge below can use them. */
export const ACCESS_TOKEN_KEY = 'bnft-access-token';
export const REFRESH_TOKEN_KEY = 'bnft-refresh-token';

/** CSRF token — readable on purpose, echoed back in the X-CSRF-Token header. */
export const CSRF_TOKEN_KEY = 'bnft-csrf-token';

/** 'cookie' | 'bearer'. Persisted so a page reload knows how to authenticate. */
export const AUTH_MODE_KEY = 'bnft-auth-mode';

export type AuthMode = 'cookie' | 'bearer';

/** Renew this many ms before the actual expiry, to absorb clock skew / latency. */
const EXPIRY_SKEW_MS = 60_000;

// ─── Safe storage access ─────────────────────────────────────────────────────
// localStorage throws in some contexts (private windows with site data blocked,
// embedded webviews). Authentication must never break because of that.

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Non-fatal: the session still works for this page load.
  }
}

function removeLocal(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Non-fatal.
  }
}

// ─── Token change notifications ──────────────────────────────────────────────
// Lets the NetworkContext resume auto-sync the moment the user re-authenticates
// (or a background refresh succeeds), without cross-tab storage-event plumbing.
//
// This must keep firing in cookie mode even though no JWT is written any more:
// it is the signal the offline auto-sync listens to. Dropping it would leave
// the sync silently idle after a reconnection.

type TokenListener = () => void;
const tokenListeners = new Set<TokenListener>();

export function onTokensChanged(listener: TokenListener): () => void {
  tokenListeners.add(listener);
  return () => {
    tokenListeners.delete(listener);
  };
}

function emitTokensChanged(): void {
  for (const listener of tokenListeners) {
    try {
      listener();
    } catch {
      // A misbehaving listener must never break session storage.
    }
  }
}

// ─── Mode ────────────────────────────────────────────────────────────────────

export function getAuthMode(): AuthMode {
  return readLocal(AUTH_MODE_KEY) === 'cookie' ? 'cookie' : 'bearer';
}

export function isCookieMode(): boolean {
  return getAuthMode() === 'cookie';
}

function setAuthMode(mode: AuthMode): void {
  writeLocal(AUTH_MODE_KEY, mode);
  // Write-through for the Service Worker: it has no localStorage, and it must
  // know which scheme to use when replaying the offline queue from a background
  // sync event. Inferring the mode from which keys happen to be present would
  // be wrong after a downgrade, when a stale CSRF token still sits alongside
  // freshly written JWTs.
  void putConfig(AUTH_MODE_KEY, mode).catch(() => {});
}

// ─── CSRF token ──────────────────────────────────────────────────────────────

export function getCsrfToken(): string | null {
  return readLocal(CSRF_TOKEN_KEY);
}

function storeCsrfToken(csrfToken: string): void {
  writeLocal(CSRF_TOKEN_KEY, csrfToken);
  // Write-through for the Service Worker, which has no localStorage. It needs
  // the token to replay queued writes from a background sync event.
  void putConfig(CSRF_TOKEN_KEY, csrfToken).catch(() => {});
}

// ─── Legacy JWT storage (bearer mode only) ───────────────────────────────────

export function getAccessToken(): string | null {
  return readLocal(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return readLocal(REFRESH_TOKEN_KEY);
}

/**
 * Remove any JWT left in browser storage.
 *
 * Called on every start-up once cookie mode is active. Merely ceasing to WRITE
 * the tokens would not have been enough: the ones already sitting in each
 * existing user's localStorage would have stayed there, exploitable, until that
 * user cleared their site data — a 30-day refresh token still readable by any
 * XSS long after the migration was declared finished.
 *
 * Idempotent, and safe to keep for at least 60 days (twice the refresh-token
 * lifetime) before it can be removed.
 */
export function purgeLegacyTokens(): void {
  removeLocal(ACCESS_TOKEN_KEY);
  removeLocal(REFRESH_TOKEN_KEY);
  void deleteConfig(ACCESS_TOKEN_KEY).catch(() => {});
  void deleteConfig(REFRESH_TOKEN_KEY).catch(() => {});
}

/**
 * Persist JWTs — BEARER MODE ONLY.
 *
 * In cookie mode the credentials live in HttpOnly cookies and this must not be
 * called; `startSession()` decides.
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  setAuthMode('bearer');
  writeLocal(ACCESS_TOKEN_KEY, accessToken);
  writeLocal(REFRESH_TOKEN_KEY, refreshToken);
  // Fire-and-forget write-through for the Service Worker.
  void putConfig(ACCESS_TOKEN_KEY, accessToken).catch(() => {});
  void putConfig(REFRESH_TOKEN_KEY, refreshToken).catch(() => {});
  emitTokensChanged();
}

// ─── Session lifecycle ───────────────────────────────────────────────────────

interface SessionCredentials {
  access_token?: string;
  refresh_token?: string;
  /** Present only when the server issued cookies. The capability signal. */
  csrf_token?: string | null;
}

/**
 * Record the session returned by /auth/login or /auth/refresh.
 *
 * A `csrf_token` in the payload means the server issued cookies: store only
 * that, and purge any JWT inherited from before the migration. Without it, fall
 * back to the legacy bearer scheme.
 */
export function startSession(credentials: SessionCredentials): AuthMode {
  if (credentials.csrf_token) {
    setAuthMode('cookie');
    storeCsrfToken(credentials.csrf_token);
    purgeLegacyTokens();
    emitTokensChanged();
    return 'cookie';
  }

  if (credentials.access_token && credentials.refresh_token) {
    setTokens(credentials.access_token, credentials.refresh_token);
    return 'bearer';
  }

  emitTokensChanged();
  return getAuthMode();
}

/**
 * Downgrade an established cookie session to bearer.
 *
 * Safety net for the one failure mode that would otherwise lock users out: the
 * server issues cookies, but the browser refuses to store or send them (a
 * Secure cookie over plain HTTP, a privacy mode blocking site data). Because
 * /auth/login still returns the JWTs in its body, the caller can hand them over
 * here and the session continues on the legacy scheme instead of dying.
 */
export function downgradeToBearer(accessToken: string, refreshToken: string): void {
  setTokens(accessToken, refreshToken);
}

export function clearTokens(): void {
  removeLocal(ACCESS_TOKEN_KEY);
  removeLocal(REFRESH_TOKEN_KEY);
  removeLocal(CSRF_TOKEN_KEY);
  removeLocal(AUTH_MODE_KEY);
  void deleteConfig(ACCESS_TOKEN_KEY).catch(() => {});
  void deleteConfig(REFRESH_TOKEN_KEY).catch(() => {});
  void deleteConfig(CSRF_TOKEN_KEY).catch(() => {});
  emitTokensChanged();
}

// ─── JWT expiry inspection (bearer mode) ─────────────────────────────────────

function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return atob(padded);
}

/** Returns the token's expiry in epoch ms, or null if it can't be parsed. */
export function getTokenExpiryMs(token: string): number | null {
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) {return null;}
    const payload = JSON.parse(base64UrlDecode(payloadSegment)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * True when the token is missing, or expired/about-to-expire (within the skew
 * window). If the token can't be decoded we assume it is still valid and let
 * the server be the judge — decoding failures must not block requests.
 */
export function isAccessTokenExpired(token: string | null, skewMs = EXPIRY_SKEW_MS): boolean {
  if (!token) {return true;}
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) {return false;}
  return Date.now() >= expiryMs - skewMs;
}

// ─── Refresh ─────────────────────────────────────────────────────────────────

/** Single-flight guard so concurrent callers share one /auth/refresh request. */
let inflightRefresh: Promise<string | null> | null = null;

/**
 * Renew the session.
 *
 * In cookie mode the refresh token travels in the HttpOnly `bnft_refresh`
 * cookie, so the body is empty and `withCredentials` does the work; the server
 * sets fresh cookies and returns an updated CSRF token. In bearer mode the
 * refresh token is sent in the body, as before.
 *
 * Resolves to a non-null string on success — the new access token in bearer
 * mode, a sentinel in cookie mode where the caller never sees a token — and to
 * null when there is no way to authenticate any more.
 *
 * The refresh endpoint does NOT rotate the refresh token (it echoes the same
 * one back), so renewing concurrently from several places — main thread and
 * Service Worker — is safe and idempotent.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) {return inflightRefresh;}

  const cookieMode = isCookieMode();
  const refreshToken = getRefreshToken();

  // Bearer mode with nothing to renew from: no point calling the server.
  if (!cookieMode && !refreshToken) {return Promise.resolve(null);}

  inflightRefresh = (async () => {
    try {
      const response = await axios.post<{
        access_token: string;
        refresh_token?: string;
        csrf_token?: string | null;
      }>(
        `${getApiBaseUrl()}/auth/refresh`,
        cookieMode ? {} : { refresh_token: refreshToken },
        { timeout: 30_000, withCredentials: true },
      );

      const data = response.data;
      if (data?.csrf_token) {
        // Still (or now) in cookie mode: refresh the CSRF token and report
        // success without exposing a JWT to the caller.
        setAuthMode('cookie');
        storeCsrfToken(data.csrf_token);
        purgeLegacyTokens();
        emitTokensChanged();
        return 'cookie-session';
      }

      const accessToken = data?.access_token;
      if (!accessToken) {return null;}
      setTokens(accessToken, data.refresh_token ?? refreshToken ?? '');
      return accessToken;
    } catch {
      // Network failure or 401 (refresh token dead) — caller decides what to do.
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

/** True when this client holds something it can authenticate with. */
export function hasSession(): boolean {
  return isCookieMode() ? !!getCsrfToken() : !!getAccessToken();
}

/**
 * Bearer access token, renewed if needed. Bearer mode only.
 *
 * Returns null in cookie mode: there is no token to hand out, the browser holds
 * the credential. Offline replay must use `getReplayAuthHeaders()` instead,
 * which covers both modes.
 */
export async function getValidAccessToken(): Promise<string | null> {
  if (isCookieMode()) {
    return null;
  }
  const current = getAccessToken();
  if (current && !isAccessTokenExpired(current)) {return current;}
  return refreshAccessToken();
}

// ─── Offline replay credentials ──────────────────────────────────────────────

/**
 * Headers that prove this client's identity on a replayed request.
 *
 * Never freeze these into a queued item. A queued write can sit for days before
 * the agent regains connectivity, long past the 30-minute access-token
 * lifetime; credentials are obtained fresh at replay time instead.
 *
 * Cookie mode returns only the CSRF header — the browser attaches the session
 * cookie itself, provided the request is sent with credentials.
 *
 * Returns null when there is no way to authenticate. Callers MUST treat that as
 * "leave the item pending", never as a failure: burning an agent's checkpoint
 * record because their session lapsed would lose field data.
 */
export async function getReplayAuthHeaders(): Promise<Record<string, string> | null> {
  if (isCookieMode()) {
    let csrfToken = getCsrfToken();
    if (!csrfToken) {
      // No CSRF token yet — a session established before the migration, or a
      // cleared storage. /auth/refresh hands one back in its body.
      await refreshAccessToken();
      csrfToken = getCsrfToken();
    }
    return csrfToken ? { 'X-CSRF-Token': csrfToken } : null;
  }

  const token = await getValidAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : null;
}

/**
 * Force a session renewal and return fresh replay headers.
 *
 * Called after a 401 (access expired mid-replay) or a 403 (CSRF token missing
 * or stale). Both are recoverable from the same place: the refresh endpoint
 * re-issues the cookies and returns an updated CSRF token.
 */
export async function renewReplayAuthHeaders(): Promise<Record<string, string> | null> {
  const renewed = await refreshAccessToken();
  if (!renewed) {return null;}
  return getReplayAuthHeaders();
}
